import AppHeader from '@standardsdigital/ui-components/AppHeader';
import Button from '@standardsdigital/ui-components/Button';
import Logo, { Client } from '@standardsdigital/ui-components/Logo';
import type { NavLinkComponentProps } from '@standardsdigital/ui-components/NavLink';
import ScreenReaderOnly from '@standardsdigital/ui-components/ScreenReaderOnly';
import type { UserMenuLink } from '@standardsdigital/ui-components/src/components/AppHeader/UserMenu/index';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { getActiveCustomer, localizePathname } from '../../../util/util';
import useGetAppUser from '../../../hooks/useGetAppUser';
import { languageLabels, supportedLanguages } from '../../../i18nConfig';
import { useGetAppLinksQuery } from 'redux/services/appLinksApi';
import { useGetAppConfigurationQuery } from 'redux/services/appConfigurationApi';
import {
  isCustomSubscriptionsEnabled,
  isServiceLevelDefinitionsEnabled,
  useLoadAdminSettingsQuery,
} from 'redux/services/settingsApi';
import { routes } from 'util/routes';

let clientCode = Client.SD;
if (Object.values(Client).includes(getActiveCustomer() as Client)) {
  clientCode = getActiveCustomer() as Client;
}

const loginUrl = '/bff/login';

function NewHeader() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const user = useGetAppUser();
  const { data: configuration } = useGetAppConfigurationQuery(undefined, {
    skip: !user,
  });
  const { data: appLinks } = useGetAppLinksQuery(undefined, { skip: !user });
  const { data: adminSettings } = useLoadAdminSettingsQuery(undefined, {
    skip: !user,
  });
  const showServiceLevels = isServiceLevelDefinitionsEnabled(adminSettings);
  const showCustomSubscriptionConfiguration =
    isCustomSubscriptionsEnabled(adminSettings);

  const userMenuLinks: UserMenuLink[] = [];

  if (user && configuration?.productHubUrl) {
    userMenuLinks.push({
      id: 1,
      link: (
        <a href={`${configuration.productHubUrl}/${i18n.language}`}>
          {t('general.productHub')}
        </a>
      ),
    });
  }

  const languages = supportedLanguages.map((language) => ({
    languageCode: language,
    component: (
      <Button
        variant="text"
        lang={language}
        key={language}
        onClick={() => i18n.changeLanguage(language)}
      >
        {languageLabels[language]}
      </Button>
    ),
  }));

  const activeLanguage = {
    code: i18n.language,
    label: languageLabels[i18n.language as keyof typeof languageLabels],
  };

  const pathnameSegments = pathname.split('/').filter(Boolean);
  const currentPathname = supportedLanguages.includes(
    pathnameSegments[0] as (typeof supportedLanguages)[number],
  )
    ? pathnameSegments.slice(1).join('/')
    : pathnameSegments.join('/');
  const normalizedCurrentPathname =
    currentPathname === '' ? routes.subscribers.path : currentPathname;

  const logo = (
    <Link to={localizePathname(routes.home.to())}>
      <ScreenReaderOnly>{t('general.home')}</ScreenReaderOnly>
      <Logo clientCode={clientCode} />
    </Link>
  );

  const userMenuProps = {
    'aria-label': t('general.userMenu'),
    'data-auto': 'user-menu',
    firstName: user ? user.givenName : undefined,
    links: userMenuLinks,
    logInLink: (
      <a data-auto="log-in-link" href={loginUrl}>
        {t('authentication.logIn')}
      </a>
    ),
    logOutLink: user ? (
      <a data-auto="log-out-link" href={user.logoutLink}>
        {t('authentication.logOut')}
      </a>
    ) : undefined,
  };

  const languageSelectorProps = {
    'data-auto': 'language-selector',
    'aria-label': t('general.selectLanguage'),
    languages,
    activeLanguage,
  };

  const appSelectorProps = {
    'data-auto': 'app-selector',
    'aria-label': t('general.selectApplication'),
    currentAppName: t('headings.subscriptionManagement'),
    appLinks: appLinks
      ? appLinks.map((appLink) => ({
          id: appLink.id,
          name: appLink.title,
          url: `${appLink.url}/${i18n.language}`,
        }))
      : [],
  };

  const navLinks: NavLinkComponentProps[] = [
    {
      link: (
        <Link to={localizePathname(routes.subscribers.to())}>
          {t('navigation.subscribers')}
        </Link>
      ),
      isCurrent: normalizedCurrentPathname === routes.subscribers.path,
    },
  ];

  if (showServiceLevels) {
    navLinks.push({
      link: (
        <Link to={localizePathname(routes.serviceLevels.to())}>
          {t('navigation.serviceLevels')}
        </Link>
      ),
      isCurrent: normalizedCurrentPathname.startsWith(routes.serviceLevels.path),
    });
  }

  if (showCustomSubscriptionConfiguration) {
    navLinks.push({
      link: (
        <Link to={localizePathname(routes.configuration.to())}>
          {t('navigation.configuration')}
        </Link>
      ),
      isCurrent: normalizedCurrentPathname === routes.configuration.path,
    });
  }

  const navLinkProps = user
    ? {
        links: navLinks,
        'aria-label': t('general.mainNavigation'),
      }
    : undefined;

  return (
    <AppHeader
      data-auto="app-header"
      logo={logo}
      hamburgerMenuButtonOpenText={t('general.mainNavigation')}
      hamburgerMenuButtonCloseText={t('general.mainNavigation')}
      compactNavigationAriaLabel={t('general.mainNavigation')}
      userMenuProps={userMenuProps}
      navLinkProps={navLinkProps}
      languageSelectorProps={languageSelectorProps}
      appSelectorProps={appSelectorProps}
    />
  );
}

export default NewHeader;
